import Head from "next/head";
import { buildAbsoluteInternalLink, setTrailingSlash } from "../../utils/url";
import { getHeader } from "@/wp/getHeader";
import {
  OptionsSecurityHeader,
  OptionsSeoSettingsType,
  TemplateRoutingSettings,
} from "@/types/OptionType";
import { Post } from "@/wp/types/client";

interface HeadMetaProps {
  post: Post;
  securityHeaderOptions: OptionsSecurityHeader;
  seoSettingsOptions: OptionsSeoSettingsType;
  routing?: TemplateRoutingSettings;
}

// TODO do not pass full options object to module only pass the props needed to render the component!!!!
export const HeadMeta = ({
  post,
  securityHeaderOptions,
  seoSettingsOptions,
  routing,
}: HeadMetaProps) => {
  const postHeader = getHeader({ post });
  const seoSettings =
    postHeader && "seo_settings" in postHeader
      ? postHeader.seo_settings
      : undefined;
  const structuredData =
    postHeader && "structured_data" in postHeader
      ? postHeader.structured_data
      : undefined;

  const wpml_current_locale = post?.wpml_current_locale || "de_DE";

  // ----- SEO CORE VALUES -----
  const pageSeoTitle = seoSettings?.seo_title || post.title?.rendered || "";
  const globalSeoTemplate = seoSettingsOptions?.seo_title || "";

  const metaTitle = globalSeoTemplate.includes("$page_seo_title")
    ? globalSeoTemplate.replace(/"\$page_seo_title"/g, pageSeoTitle)
    : globalSeoTemplate || pageSeoTitle;
  const metaTitleFinal = metaTitle?.trim() || pageSeoTitle?.trim() || "";
  const metaDescriptionFinal = (
    seoSettings?.seo_metadescription ||
    seoSettingsOptions?.page_description_fallback ||
    ""
  ).trim();

  const shareDescriptionFinal = (
    seoSettings?.social_media_share_description ||
    metaDescriptionFinal ||
    ""
  ).trim();

  // ----- noIndex globalsetting + routing + case "page" -----
  const postType = post.type;
  let hasSinglePage: boolean | undefined;
  if (
    (postType === "videos" || postType === "audios") &&
    postHeader &&
    "has_single_page" in postHeader
  ) {
    hasSinglePage = postHeader.has_single_page;
  }

  const noIndex = shouldNoIndex({
    postType,
    seoSettings,
    seoSettingsOptions,
    routing,
    hasSinglePage,
  });
  const robotsDirectives = [
    noIndex ? "noindex" : "index",
    noIndex ? "nofollow" : "follow",
    "max-snippet:-1",
    "max-image-preview:large",
    "max-video-preview:-1",
  ].join(",");

  // ----- SHARE IMAGE -----
  const seoImage = seoSettings?.social_media_share_image || false;
  const globalFallbackImage =
    seoSettingsOptions?.social_media_share_image_fallback || false;

  const OG_IMAGE = seoImage || globalFallbackImage || false;

  // ----- LOCALE / URL / SITE NAME -----
  const ogLocale = wpml_current_locale;
  const canonicalUrl = post.link
    ? buildAbsoluteInternalLink(post.link)
    : undefined;

  const canonicalUrlFinal = canonicalUrl
    ? setTrailingSlash(canonicalUrl)
    : undefined;

  const fullOgTitle = metaTitleFinal;

  const modifiedTime = post.modified_gmt || post.modified;

  const twitterCard = OG_IMAGE ? "summary_large_image" : "summary";
  const ogType = resolveOgType(post.type, seoSettingsOptions);

  // ----- SECURITY POLICY -----
  const scriptURLs =
    securityHeaderOptions?.content_security_policy_header?.script_urls;
  const imgURLs =
    securityHeaderOptions?.content_security_policy_header?.image_urls;
  const connectURLs =
    securityHeaderOptions?.content_security_policy_header?.connect_urls;
  const iframeURLs =
    securityHeaderOptions?.content_security_policy_header?.iframe_urls;
  const mediaURLs =
    securityHeaderOptions?.content_security_policy_header?.media_urls;
  const fontURLs =
    securityHeaderOptions?.content_security_policy_header?.font_urls;
  const styleURLs =
    securityHeaderOptions?.content_security_policy_header?.style_urls;
  const cspContent = `
  script-src 'self' 'unsafe-inline' 'unsafe-eval' ${scriptURLs ? scriptURLs.join(" ") : ""};
  style-src 'self' 'unsafe-inline' ${styleURLs ? styleURLs.join(" ") : ""};
  img-src 'self' ${process.env.WP_API_URL} ${imgURLs ? imgURLs.join(" ") : ""} data:;
  font-src 'self' ${fontURLs ? fontURLs.join(" ") : ""} data:; 
  connect-src 'self' ${process.env.WP_API_URL} ${connectURLs ? connectURLs.join(" ") : ""};
  frame-src 'self' ${iframeURLs ? iframeURLs.join(" ") : ""};
  media-src 'self' ${mediaURLs ? mediaURLs.join(" ") : ""};
`
    .replace(/\s{2,}/g, " ")
    .trim(); // Minify CSP

  return (
    <Head>
      <link rel="dns-prefetch" href={process.env.WP_API_URL} />
      <link rel="preconnect" href={process.env.WP_API_URL} />
      <link
        rel="preconnect"
        href={process.env.WP_API_URL}
        crossOrigin="anonymous"
      />
      <link
        rel="icon"
        type="image/png"
        href="/favicon-96x96.png"
        sizes="96x96"
      />
      <link rel="icon" type="image/svg+xml" href="/favicon.svg" />
      <link rel="shortcut icon" href="/favicon.ico" />
      <link
        rel="apple-touch-icon"
        sizes="180x180"
        href="/apple-touch-icon.png"
      />
      <link
        rel="manifest"
        href="/site.webmanifest"
        crossOrigin="use-credentials"
      />
      {/* Title */}
      {metaTitleFinal && <title>{metaTitleFinal}</title>}
      <meta charSet="UTF-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      {metaDescriptionFinal && (
        <meta name="description" content={metaDescriptionFinal} />
      )}

      {/* Robots */}
      <meta name="robots" content={robotsDirectives} />

      {/* OG Image */}
      {OG_IMAGE && OG_IMAGE?.url && (
        <>
          <meta property="og:image" content={OG_IMAGE.url} />
          {OG_IMAGE.width && (
            <meta
              property="og:image:width"
              content={OG_IMAGE.width.toString()}
            />
          )}
          {OG_IMAGE.height && (
            <meta
              property="og:image:height"
              content={OG_IMAGE.height.toString()}
            />
          )}
        </>
      )}
      {/* OpenGraph */}
      {ogLocale && <meta property="og:locale" content={ogLocale} />}
      {ogType && <meta property="og:type" content={ogType} />}
      {metaTitleFinal && <meta property="og:title" content={metaTitleFinal} />}
      {shareDescriptionFinal && (
        <meta property="og:description" content={shareDescriptionFinal} />
      )}
      {canonicalUrlFinal && (
        <meta property="og:url" content={canonicalUrlFinal} />
      )}

      {modifiedTime && (
        <meta
          property="article:modified_time"
          content={new Date(modifiedTime).toISOString()}
        />
      )}
      {/* Canonical */}
      {canonicalUrlFinal && <link rel="canonical" href={canonicalUrlFinal} />}

      {/* Twitter */}
      <meta name="twitter:card" content={twitterCard} />
      {fullOgTitle && <meta name="twitter:title" content={fullOgTitle} />}
      {shareDescriptionFinal && (
        <meta name="twitter:description" content={shareDescriptionFinal} />
      )}

      {/* Current language */}
      {post.link && (
        <link
          rel="alternate"
          //if wpml is not setup (single language project) fallback to first of NEXT_PUBLIC_LOCALES
          //if NEXT_PUBLIC_LOCALES env is not set fallback to "de"
          //TODO: for SINGLE LANGUAGE project which is NOT GERMAN: set NEXT_PUBLIC_LOCALES or change the fallback language from "de" to the actual language
          hrefLang={
            wpml_current_locale.split("_")[0] ??
            (process.env.NEXT_PUBLIC_LOCALES !== ""
              ? process.env.NEXT_PUBLIC_LOCALES?.split(",")[0]
              : undefined) ??
            "de"
          }
          href={buildAbsoluteInternalLink(post.link)}
        />
      )}

      {/* Other available languages */}
      {post.wpml_translations?.map((translation, key) => {
        return (
          translation.href &&
          translation.locale && (
            <link
              key={key}
              rel="alternate"
              hrefLang={translation.locale.split("_")[0]}
              href={buildAbsoluteInternalLink(translation.href)}
            />
          )
        );
      })}
      {/* Structured data (from header) */}
      {structuredData?.trim?.() ? (
        <script type="application/ld+json">{structuredData}</script>
      ) : null}
      {/* Global company schema from options */}
      {seoSettingsOptions?.company_schema_data?.trim() && (
        <script type="application/ld+json">
          {seoSettingsOptions.company_schema_data}
        </script>
      )}

      {securityHeaderOptions?.enable_content_security_policy && cspContent && (
        <meta httpEquiv="Content-Security-Policy" content={cspContent} />
      )}
      {securityHeaderOptions?.referrer_policy && (
        <meta name="referrer" content="no-referrer" />
      )}
    </Head>
  );
};

function resolveOgType(
  postType: Post["type"],
  options: OptionsSeoSettingsType,
): string {
  const ogRepeater = options?.og_type || [];

  // Special cases for post types
  if (postType === "videos") return "video";
  if (postType === "audios") return "audio";

  // Check og_type repeater
  for (const row of ogRepeater) {
    if (row.post_type.includes(postType)) {
      return row.type; // "article"| "website" |"profile"
    }
  }

  // Fallback
  return "website";
}

type SeoHeaderSettings = {
  exclude_from_search_engines?: boolean;
};

export const shouldNoIndex = ({
  postType,
  seoSettings,
  seoSettingsOptions,
  routing,
  hasSinglePage,
}: {
  postType: Post["type"];
  seoSettings?: SeoHeaderSettings;
  seoSettingsOptions: OptionsSeoSettingsType;
  routing?: TemplateRoutingSettings;
  hasSinglePage?: boolean;
}): boolean => {
  const globalDisableIndexing = seoSettingsOptions?.disable_indexing === true;

  if (globalDisableIndexing || postType === "catalog") {
    return true;
  }

  if (postType === "page") {
    // Pages have always single view -> check header
    return seoSettings?.exclude_from_search_engines === true;
  }

  // Other post types check routing
  const routingConfig = routing?.[postType];
  const routingSettings = routingConfig?.settings;

  const hasSettingsObject =
    routingSettings && typeof routingSettings === "object";

  if (!hasSettingsObject) {
    return true;
  }

  const toggleKey = `toggle_cpt_${postType}_single`;
  const isSingleEnabled =
    (routingSettings as Record<string, unknown>)[toggleKey] === true;

  if (!isSingleEnabled) {
    return true;
  }
  if (
    (postType === "videos" || postType === "audios") &&
    hasSinglePage === false
  ) {
    return true;
  }
  // If has single view -> check header
  return seoSettings?.exclude_from_search_engines === true;
};
